import type {
    SimulationDetailTO,
    SimulationStateNameTO,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import { SimulationStateNameTO as StateNames } from '@control-center/control-center-client-public-domain-simulation-ts';
import ProgressBar, { type ProgressBarColor } from 'components/feedback/ProgressBar';
import Tooltip from 'components/overlays/Tooltip';
import { diffInSeconds, formatDurationClockTime } from 'helper/time';
import { useEffect, useState } from 'react';

import SimulationStateBadge from './SimulationStateBadge';

const getProgressProps = (
    stateName: SimulationStateNameTO,
    stateDetail: SimulationDetailTO['state'],
    totalSteps: number
): { percentage: number; color: ProgressBarColor; step?: number } => {
    switch (stateName) {
        case StateNames.Running: {
            const step = stateDetail.currentStepIndex ?? 0;
            return { percentage: Math.min((step / totalSteps) * 100, 100), color: 'progress-bar-primary', step };
        }
        case StateNames.Overridden: {
            const step = stateDetail.savedStepIndex ?? 0;
            return { percentage: Math.min((step / totalSteps) * 100, 100), color: 'progress-bar-danger', step };
        }
        case StateNames.Completed:
            return { percentage: 100, color: 'progress-bar-success', step: totalSteps };
        default:
            return { percentage: 0, color: 'bg-gray' };
    }
};

interface Props {
    state: SimulationStateNameTO;
    stateDetail: SimulationDetailTO['state'];
    isOnline: boolean;
    numberOfSteps: number;
    startedAt: string | null | undefined;
}

const SimulationProgressWidget = ({ state, stateDetail, isOnline, numberOfSteps, startedAt }: Props) => {
    const [elapsedTime, setElapsedTime] = useState<string | null>(null);

    useEffect(() => {
        if (!startedAt || state === StateNames.Completed) {
            setElapsedTime(null);
            return;
        }
        const startDate = new Date(startedAt);
        const update = () => setElapsedTime(formatDurationClockTime(diffInSeconds(new Date(), startDate)));
        update();
        const id = setInterval(update, 1000);
        return () => clearInterval(id);
    }, [state, startedAt]);

    const {
        percentage: progressPercent,
        color: progressColor,
        step: progressStep,
    } = getProgressProps(state, stateDetail, numberOfSteps);

    const remainingSteps = progressStep !== undefined ? numberOfSteps - progressStep : undefined;

    return (
        <div className='SimulationDetailSidebar__progressWidget' data-testid='domain-sim-progress'>
            <div className='SimulationDetailSidebar__barTopRow'>
                <SimulationStateBadge state={state} />
                <div className='SimulationDetailSidebar__onlineIndicator SimulationDetailSidebar__barCell--center'>
                    {isOnline ? (
                        <>
                            <span className='rioglyph rioglyph-wifi text-primary' />
                            <span>Online</span>
                        </>
                    ) : (
                        <>
                            <span className='rioglyph rioglyph-wifi-off' />
                            <span>Offline</span>
                        </>
                    )}
                </div>
                <span className='SimulationDetailSidebar__barCell--right'>{Math.round(progressPercent)}%</span>
            </div>
            <ProgressBar progress={[{ percentage: progressPercent, color: progressColor }]} />
            <div className='SimulationDetailSidebar__barBottomRow'>
                <div className='SimulationDetailSidebar__barMetaItem'>
                    <span className='SimulationDetailSidebar__barMetaLabel'>Elapsed</span>
                    <span className='SimulationDetailSidebar__elapsedTime' data-testid='domain-sim-elapsed-time'>
                        {elapsedTime ?? '—'}
                    </span>
                </div>
                <div className='SimulationDetailSidebar__barMetaItem SimulationDetailSidebar__barMetaItem--center'>
                    <span className='SimulationDetailSidebar__barMetaLabel'>Remaining</span>
                    <span className='SimulationDetailSidebar__barMetaValue'>
                        {remainingSteps !== undefined ? (
                            <Tooltip trigger={formatDurationClockTime(remainingSteps)} placement='top'>
                                <Tooltip.Content>{remainingSteps} scenario steps</Tooltip.Content>
                            </Tooltip>
                        ) : (
                            '—'
                        )}
                    </span>
                </div>
                <div className='SimulationDetailSidebar__barMetaItem SimulationDetailSidebar__barMetaItem--right'>
                    <span className='SimulationDetailSidebar__barMetaLabel'>Total</span>
                    <span className='SimulationDetailSidebar__barMetaValue'>
                        <Tooltip trigger={formatDurationClockTime(numberOfSteps)} placement='top'>
                            <Tooltip.Content>{numberOfSteps} scenario steps</Tooltip.Content>
                        </Tooltip>
                    </span>
                </div>
            </div>
            {state === StateNames.Overridden &&
                (stateDetail.overrideReason ||
                    stateDetail.mrmPhase ||
                    stateDetail.mrmPhaseStepsRemaining !== undefined) && (
                    <div className='SimulationDetailSidebar__barOverrideRow'>
                        {stateDetail.overrideReason && (
                            <div className='SimulationDetailSidebar__barMetaItem'>
                                <span className='SimulationDetailSidebar__barMetaLabel'>Reason</span>
                                <span className='SimulationDetailSidebar__overrideReasonBadge'>
                                    {stateDetail.overrideReason}
                                </span>
                            </div>
                        )}
                        {stateDetail.mrmPhase && (
                            <div className='SimulationDetailSidebar__barMetaItem'>
                                <span className='SimulationDetailSidebar__barMetaLabel'>MRM phase</span>
                                <span className='SimulationDetailSidebar__barMetaValue'>{stateDetail.mrmPhase}</span>
                            </div>
                        )}
                        {stateDetail.mrmPhaseStepsRemaining !== undefined && (
                            <div className='SimulationDetailSidebar__barMetaItem'>
                                <span className='SimulationDetailSidebar__barMetaLabel'>MRM steps left</span>
                                <span className='SimulationDetailSidebar__barMetaValue'>
                                    {stateDetail.mrmPhaseStepsRemaining}
                                </span>
                            </div>
                        )}
                    </div>
                )}
        </div>
    );
};

export default SimulationProgressWidget;
